import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO27017_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO/IEC 27017 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Cloud Security Controls — Guidelines for information security
            controls applicable to the provision and use of cloud services.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO/IEC 27017 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO/IEC 27017 is the international standard that provides cloud
              security controls for both cloud service providers (CSPs) and
              cloud service customers (CSCs). It extends the framework
              established by ISO/IEC 27001 (Information Security Management
              System - ISMS) by offering specific guidelines and best practices
              to address security risks associated with cloud computing. ISO/IEC
              27017 helps organizations implement secure cloud services, ensure
              data protection, and manage cloud security risks effectively.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Achieving ISO/IEC 27017 certification demonstrates a commitment to
              best practices in cloud security, ensuring that cloud environments
              are managed in a way that aligns with global security standards.
              The certification builds trust with customers by ensuring that
              both data protection and privacy are prioritized in the cloud,
              fostering confidence in the organization’s cloud-based operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO/IEC 27017 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO/IEC 27017 is an international standard for cloud security that
              provides guidelines and controls for organizations that operate
              cloud services or use cloud computing resources. It is designed to
              help both cloud service providers (CSPs) and cloud service
              customers (CSCs) manage the unique security risks associated with
              cloud environments.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              ISO/IEC 27017 offers guidance on:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Cloud security governance and management: Establishing clear roles, responsibilities, and processes for managing cloud security risks.",
                "Data security and privacy: Ensuring data protection, confidentiality, and compliance with privacy regulations in cloud environments.",
                "Access control and identity management: Implementing strong authentication and access management to protect cloud-based data and services.",
                "Risk assessment and management: Identifying and mitigating security risks associated with cloud services.",
                "Incident management: Defining processes for detecting, responding to, and recovering from cloud-related security incidents.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO/IEC 27017 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Cloud-Specific Security Guidelines",
                desc: "ISO/IEC 27017 provides cloud-specific security controls and guidelines that address both the responsibilities of cloud service providers (CSPs) and cloud service customers (CSCs). These guidelines help manage the unique security challenges posed by cloud computing, such as multi-tenancy, data ownership, and data portability.",
              },
              {
                icon: Users,
                title: "Data Protection and Privacy",
                desc: "The standard includes data protection measures, ensuring that cloud services comply with privacy regulations and data protection laws (e.g., GDPR, CCPA). It also emphasizes the need for cloud providers to implement data encryption, access controls, and data integrity checks to ensure the confidentiality and availability of customer data.",
              },
              {
                icon: Target,
                title: "Access Control and Identity Management",
                desc: "ISO/IEC 27017 promotes strong identity management and access controls within cloud environments. This includes implementing multi-factor authentication, role-based access controls (RBAC), and strong password policies to ensure that only authorized individuals can access sensitive cloud data and services.",
              },
              {
                icon: BarChart3,
                title: "Risk Management and Incident Response",
                desc: "The standard provides guidelines on identifying and mitigating cloud security risks, including the risks associated with data loss, unauthorized access, and service disruptions. It also helps organizations establish incident response plans to detect and respond to security breaches.",
              },
              {
                icon: Globe,
                title: "Cloud Contractual and Legal Considerations",
                desc: "ISO/IEC 27017 includes guidelines for ensuring that contracts with cloud service providers include appropriate security clauses to address data protection, service availability, and disaster recovery.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why ISO/IEC 27017 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Ensure Cloud Security and Compliance — ISO/IEC 27017 certification ensures that cloud services are managed securely, reducing risks associated with data breaches, cyberattacks, and non-compliance with data protection regulations.",
                "Build Customer Trust — Achieving ISO/IEC 27017 certification helps build trust with customers and partners by demonstrating a commitment to cloud security, data protection, and privacy. Customers are more likely to engage with cloud service providers who have adopted industry-recognized security controls.",
                "Manage Cloud Security Risks — ISO/IEC 27017 helps businesses identify and manage the unique risks associated with cloud environments, including issues related to data sharing, service continuity, and third-party access.",
                "Competitive Advantage — With the increasing demand for secure cloud services, ISO/IEC 27017 certification gives organizations a competitive edge by demonstrating their ability to meet cloud security best practices, positioning them as trusted cloud service providers.",
                "Improve Incident Response and Recovery — ISO/IEC 27017 helps organizations implement incident response and business continuity strategies that ensure quick recovery from disruptions or cyber incidents, ensuring minimal downtime and loss of data.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does ISO/IEC 27017 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO/IEC 27017 focuses on the specific security controls and
              guidelines needed for managing cloud-based services. The key
              components include:
            </p>
            <div className="space-y-4">
              {[
                "1. Cloud Security Governance — Establishing clear security governance for cloud environments, including roles and responsibilities for both cloud service providers and cloud service customers.",
                "2. Access Control and Data Privacy — Implementing strong access control mechanisms, identity management practices, and data privacy measures to protect cloud-hosted data, ensuring that only authorized users can access sensitive information.",
                "3. Security for Cloud Contracts — Ensuring that cloud service agreements contain appropriate clauses related to security, privacy, and service continuity. This also includes addressing subcontracting risks and managing the relationship between the cloud provider and third-party vendors.",
                "4. Cloud Security Risk Management — Identifying and managing security risks unique to cloud computing, such as data segregation in multi-tenant environments, and ensuring that risks are addressed through appropriate controls.",
                "5. Incident Management and Recovery — Developing incident response plans and disaster recovery strategies that address cloud service failures, security breaches, or data loss events, ensuring rapid recovery and minimal disruption to service delivery.",
                "6. Cloud Data Encryption — Implementing encryption for data at rest and in transit to ensure that cloud-hosted data remains confidential and protected from unauthorized access.",
                "7. Monitoring and Auditing Cloud Services — Implementing regular monitoring and auditing processes to assess the effectiveness of security controls and ensure compliance with ISO/IEC 27017.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of ISO/IEC 27017 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Enhanced Cloud Security",
                "Regulatory Compliance",
                "Customer Confidence",
                "Risk Management and Business Continuity",
                "Competitive Advantage",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve ISO/IEC 27017 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              The process of obtaining ISO/IEC 27017 certification typically
              includes the following steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Conduct a Risk Assessment — Identify the security risks associated with your cloud services and assess their potential impact on data security, service delivery, and business operations.",
                "2. Develop Cloud Security Policies and Procedures — Establish and implement cloud-specific security policies and procedures that align with the guidelines in ISO/IEC 27017, ensuring comprehensive protection for your cloud environment.",
                "3. Implement Security Controls — Implement the necessary security controls to protect cloud-hosted data, ensure secure access, and protect data privacy.",
                "4. Internal Audit and Review — Conduct internal audits to assess the effectiveness of your cloud security management practices and ensure they meet the requirements of ISO/IEC 27017.",
                "5. Third-Party Certification Audit — Engage an accredited certification body to verify that your cloud security practices comply with the ISO/IEC 27017 standard.",
                "6. Continuous Monitoring and Improvement — Regularly monitor and evaluate your cloud security controls to ensure their effectiveness and implement improvements based on audit results and emerging security threats.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO/IEC 27017 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. offers expert ISO/IEC 27017 certification
              consultancy services, helping businesses develop and implement
              comprehensive cloud security management systems (ISMS) that ensure
              data protection and compliance with global security standards.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "ISO/IEC 27017 Gap Analysis",
                "Cloud Security Risk Assessment",
                "Cloud Security Control Implementation",
                "Policy and Procedure Development",
                "Internal Audit and Pre-certification Support",
                "Third-Party Certification Assistance",
                "Ongoing Monitoring and Continuous Improvement",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve ISO/IEC 27017 certification, ensuring
              secure cloud services, data privacy, and regulatory compliance.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO27017_Details;
